import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ErrorState, Skeleton, EmptyState } from '@vta/ui';
import type { Program, TeamSummary } from '@vta/types';
import { apiClient, ApiError } from '../lib/api-client.ts';
import { OgMeta } from '../lib/og-meta.tsx';
import { CountryHeader } from '../features/countries/CountryHeader.tsx';
import { CountryCitiesList, type CountryCityRow } from '../features/countries/CountryCitiesList.tsx';
import { CityTeamList } from '../features/cities/CityTeamList.tsx';

interface CountryResponse {
  country: string;
  countrySlug: string;
  teamCount: number;
  cityCount: number;
  programBreakdown: Partial<Record<Program, number>>;
  cities: CountryCityRow[];
}

interface TeamsResponse {
  teams: TeamSummary[];
}

export const Route = createFileRoute('/country/$countrySlug')({
  loader: async ({ params }) => {
    try {
      return await apiClient.get<CountryResponse>(`/api/countries/${encodeURIComponent(params.countrySlug)}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },
  component: CountryRoute,
});

function CountryRoute() {
  const { countrySlug } = Route.useParams();
  const initial = Route.useLoaderData() as CountryResponse | null;

  const country = useQuery<CountryResponse | null>({
    queryKey: ['country', countrySlug],
    initialData: initial,
    queryFn: ({ signal }) =>
      apiClient
        .get<CountryResponse>(`/api/countries/${encodeURIComponent(countrySlug)}`, { signal })
        .catch((err) => {
          if (err instanceof ApiError && err.status === 404) return null;
          throw err;
        }),
  });

  // Pull a paged team list for the country (teams endpoint accepts country
  // filter). Limit keeps payload reasonable; the explore page is the place
  // for the full table.
  const teams = useQuery<TeamsResponse>({
    queryKey: ['country-teams', countrySlug],
    enabled: !!country.data,
    queryFn: ({ signal }) =>
      apiClient.get<TeamsResponse>('/api/teams', {
        query: { country: countrySlug, limit: 100 },
        signal,
      }),
  });

  if (country.isLoading) return <CountrySkeleton />;
  if (country.isError) {
    return (
      <main className="container mx-auto py-10">
        <ErrorState title="Could not load this country" retry={() => country.refetch()} />
      </main>
    );
  }

  const data = country.data;
  if (!data) {
    return (
      <main className="container mx-auto py-10">
        <EmptyState title="Country not found" description="The slug may be wrong." />
      </main>
    );
  }

  const description = `${data.teamCount} VEX teams across ${data.cityCount} cities in ${data.country}.`;

  return (
    <>
      <OgMeta title={data.country} description={description} pathname={`/country/${data.countrySlug}`} />
      <main className="container mx-auto flex flex-col gap-10 py-8 md:py-12">
        <CountryHeader
          country={data.country}
          countrySlug={data.countrySlug}
          teamCount={data.teamCount}
          cityCount={data.cityCount}
          programBreakdown={data.programBreakdown}
        />

        <div className="grid gap-10 lg:grid-cols-[2fr_1fr]">
          <Section title="Cities" subtitle={`${data.cityCount} catalogued`}>
            <CountryCitiesList cities={data.cities} />
          </Section>

          <aside className="flex flex-col gap-3">
            <header className="flex items-baseline justify-between gap-3 border-b border-border-subtle pb-2">
              <h2 className="font-display text-xl text-fg">Globe preview</h2>
              <p className="text-2xs uppercase tracking-[0.18em] text-fg-subtle">Country bbox</p>
            </header>
            <GlobePreviewFallback country={data.country} />
          </aside>
        </div>

        <Section title="Teams" subtitle={`Showing up to 100 of ${data.teamCount}`}>
          {teams.isLoading ? (
            <Skeleton className="h-72 w-full" />
          ) : teams.isError ? (
            <ErrorState title="Could not load teams" retry={() => teams.refetch()} />
          ) : (
            <CityTeamList teams={teams.data?.teams ?? []} />
          )}
        </Section>
      </main>
    </>
  );
}

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <header className="flex items-baseline justify-between gap-3 border-b border-border-subtle pb-2">
        <h2 className="font-display text-xl text-fg">{title}</h2>
        {subtitle ? (
          <p className="text-2xs uppercase tracking-[0.18em] text-fg-subtle">{subtitle}</p>
        ) : null}
      </header>
      {children}
    </section>
  );
}

function GlobePreviewFallback({ country }: { country: string }) {
  return (
    <div className="relative aspect-square w-full overflow-hidden rounded-lg border border-border-subtle bg-bg-raised">
      <Skeleton className="absolute inset-0 rounded-none" />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-4 text-center">
        <p className="text-2xs uppercase tracking-[0.22em] text-fg-subtle">Live preview coming online</p>
        <p className="text-xs text-fg-muted">{country}</p>
      </div>
    </div>
  );
}

function CountrySkeleton() {
  return (
    <main className="container mx-auto flex flex-col gap-6 py-10">
      <Skeleton className="h-12 w-1/2" />
      <Skeleton className="h-5 w-1/3" />
      <div className="mt-4 grid gap-6 lg:grid-cols-[2fr_1fr]">
        <Skeleton className="h-72 w-full" />
        <Skeleton className="aspect-square w-full" />
      </div>
    </main>
  );
}
